export const verifyCallbackUrlOrigin = (currentHost: string, callbackUrl: string): boolean => {
  let url: URL;
  try {
    url = new URL(callbackUrl);
  } catch (e) {
    return false;
  }

  const isLocalhost = /^(localhost|127\.0\.0\.1)(:\d+)?$/i.test(currentHost);

  // PROTOCOL ALLOW-LIST — the primary DOM-XSS guard (MSRC 142649). Only ever trust an https callback
  // (http on localhost for dev). This rejects javascript:, data:, etc. outright. A host-only check is
  // NOT enough: `javascript://ui.ads.microsoft.com/%0aalert(1)//` parses with a MATCHING host, so
  // without this the crafted URL passes and executes when the IdP button navigates to it.
  if (url.protocol !== 'https:' && !(isLocalhost && url.protocol === 'http:')) {
    return false;
  }

  // Reject embedded credentials (https://evil@host) — confusing/opaque origins.
  if (url.username || url.password) {
    return false;
  }

  if (isLocalhost) {
    // allow any https/http host if running on localhost for development purposes
    return true;
  }

  // Same-site: the callback host must equal the current host, or be a parent domain of it at a
  // DOMAIN-LABEL boundary (e.g. current ui.ads.microsoft.com, callback ads.microsoft.com). A bare
  // endsWith(url.host) also matches a partial-label attacker domain such as "crosoft.com"
  // ("...microsoft.com".endsWith("crosoft.com") === true); requiring a leading '.' closes that.
  const current = currentHost.toLowerCase();
  const target = url.host.toLowerCase();
  if (!target) {
    return false;
  }
  return current === target || current.endsWith(`.${target}`);
};

export const getRedirectUrl = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  const encodedCallback = urlParams.get('cb') || '';
  let decodedUrl = '';

  try {
    decodedUrl = decodeURIComponent(encodedCallback);
  } catch {
    return '';
  }

  if (verifyCallbackUrlOrigin(window.location.host, decodedUrl)) {
    return decodedUrl;
  }
  return '';
};

export const getRURL = (search = window.location.search): string => {
  const urlParams = new URLSearchParams(search);
  const match = Array.from(urlParams.entries()).find(([key]) => key.toLowerCase() === 'rurl');
  return match ? match[1] : '';
};

// Reads the `csle` (ClientSideLoginError) test alias from the current page URL. When present it is
// stamped onto the post-auth /login/live landing so the server can force the new vs legacy
// login-error page for testing before the pilot ramps (server: UserWorkflowHelper
// ResolveClientSideLoginErrorDecision). Returns '' when absent.
export const getCsle = (search = window.location.search): string => {
  const urlParams = new URLSearchParams(search);
  const match = Array.from(urlParams.entries()).find(([key]) => key.toLowerCase() === 'csle');
  return match ? match[1] : '';
};

// The pre-auth landing page is served from the campaign-UI host (e.g. ui.beta.ads-int.microsoft.com),
// but the invitation APIs live on the CCUI host (e.g. beta.ads-int.microsoft.com). A same-origin
// relative request would 302 to /error/pagenotfound. The `cb` callback URL's origin is the CCUI
// host, so derive the API base from it. Returns '' (=> same-origin relative request) when:
//   - running on localhost (dev proxy serves the API same-origin),
//   - there is no/invalid callback,
//   - the callback is not same-site as the current host (guards against pointing at an attacker host),
//   - the callback origin already matches the current origin.
export const getInvitationApiBaseUrl = (
  search = window.location.search,
  currentHref = window.location.href
): string => {
  let current: URL;
  try {
    current = new URL(currentHref);
  } catch {
    return '';
  }

  const currentHost = current.host;
  if (currentHost.match(/^(localhost|127\.0\.0\.1)(:\d+)?$/i)) {
    return '';
  }

  const urlParams = new URLSearchParams(search);
  const encodedCallback = urlParams.get('cb') || '';
  let decodedUrl = '';
  try {
    decodedUrl = decodeURIComponent(encodedCallback);
  } catch {
    return '';
  }

  if (!decodedUrl || !verifyCallbackUrlOrigin(currentHost, decodedUrl)) {
    return '';
  }

  try {
    const callbackOrigin = new URL(decodedUrl).origin;
    return callbackOrigin === current.origin ? '' : callbackOrigin;
  } catch {
    return '';
  }
};

export const setIdP = (url: string, provider: string): string => {
  let urlObj: URL;
  try {
    urlObj = new URL(url);
  } catch {
    urlObj = new URL(window.location.href);
  }

  urlObj.searchParams.set('idP', provider);
  return urlObj.toString();
};

// Stamps the pre-auth invitation intent ("create" = the user picked "Create an account")
// onto the redirect (cb) URL so it survives the external-login round-trip. The post-auth
// accept-invitation page reads it back (see accept-invitation-page query-params `intent`)
// to decide whether to surface the "An existing profile was found" dialog. The server must
// carry this param from /ActivateMsaUser onto the /pmaxlite/acceptinvitation redirect
// (server change S-1).
export const setInvitationIntent = (url: string, intent: string): string => {
  let urlObj: URL;
  try {
    urlObj = new URL(url);
  } catch {
    urlObj = new URL(window.location.href);
  }

  urlObj.searchParams.set('intent', intent);
  return urlObj.toString();
};
